import { TFunction } from "i18next";
import {
  ArrowRightArrowLeft,
  MobileSignalOut,
  ChartMixed,
  CodeSimple,
  LinkSimple,
  MoneyBill,
  Megaphone,
  House,
  Shield,
  Headset,
  Gear,
  ArrowRightFromBracket,
  PenToSquare,
  CommentsQuestionCheck,
  Receipt,
  Box,
  LifeRing,
  Globe,
  ArrowsRepeat,
  Users,
  Qrcode,
  MoneySimpleFromBracket,
  MoneyBillTransfer,
} from "@unified-checkout/design-system/icons/index";
import type { IAccountBarLinks, ICreateBtnLinks, INavLinks, ISupportLinks } from "../../types/innerLayout/nav-links";
import { NAVBAR_SPECIAL_ACTIONS } from "@app/dashboard/constants/navbar-special-actions";
import { LOCAL_STORAGE_KEYS } from "@app/dashboard/constants/local-storage-keys";
import { PERMISSIONS } from "@app/dashboard/constants/permissions";
import { PROFILE } from "@app/dashboard/constants/profile";
import { INNER_ROUTES } from "@app/dashboard/constants/inner-routes";
import { getFromLocalStorage } from "@app/dashboard/utils/manageLocalStorage";
import { isAccessible } from "@app/dashboard/utils/isAllowedPermission";
import { isAllowedCountry } from "@app/dashboard/utils/isAllowedCountry";
import { COUNTRIES } from "@app/dashboard/constants/countries";
import { getProfileType, isBillsEnabled } from "@app/dashboard/utils/localStorageUtils";
import { getJoyRideElementId } from "@app/dashboard/components/inner_layout/DashboardGuide/utils";
import { SHARED_EVENTS_VALUES } from "@app/dashboard/constants/tracking_events/shared";

const {
  HOME,
  BALANCE,
  TRANSFERS,
  SINGLE_TRANSFER,
  TRANSACTIONS,
  PAYME,
  PAYME_SINGLE_TRANSACTION,
  PAYME_SETTINGS,
  SINGLE_TRANSACTION,
  ORDERS,
  SINGLE_ORDER,
  CHARGE_BACKS,
  SINGLE_CHARGE_BACK,
  QUICK_LINKS,
  SINGLE_QUICK_LINK,
  CAMPAIGNS,
  SINGLE_CAMPAIGN,
  CAMPAIGN_ANALYSIS,
  BUNDLES,
  REPORTS,
  RISK_CENTER,
  SINGLE_RISK_ITEM,
  DEVICES,
  BRANCHES,
  SUMMARY,
  SETTINGS,
  BULK_PAYMENT_LINKS,
  SINGLE_PAYMENT_LINK,
  DISPUTES,
  SINGLE_DISPUTE
} = INNER_ROUTES;

const isStaff = () => getProfileType() === PROFILE.TYPE.MERCHANT_STAFF;
const isMerchant = () => getProfileType() === PROFILE.TYPE.MERCHANT;

export const navLinks: (t: TFunction) => INavLinks[] = (t) => {

  const getNavItemIdentifiers = (key: string) => ({
    label: t(key),
    id: t(key, { lng: 'en' })
  });

  const isTransfersAllowed = isAccessible(PERMISSIONS.CAN_VIEW_TRANSFERS);
  const isTrnxAllowed = isAccessible(PERMISSIONS.CAN_VIEW_TRANSACTIONS);
  const isOrdersAllowed = isAccessible(PERMISSIONS.CAN_VIEW_ORDERS);
  const isChargebacksAllowed = isAccessible(PERMISSIONS.CAN_HANDLE_CHARGE_BACKS);
  const canViewPaymentsTab = isTrnxAllowed || isOrdersAllowed || isChargebacksAllowed;
  const isQuickLinksAllowed = isStaff() ? isAccessible(PERMISSIONS.CAN_VIEW_QUICK_LINKS) : true;
  const isPaymeAllowed = isStaff() ? isAccessible(PERMISSIONS.CAN_VIEW_PAYMENT_HANDLE) : true;
  const canViewPaymentLinksTab = isQuickLinksAllowed || isPaymeAllowed;
  const isReportsAllowed = isAccessible(PERMISSIONS.CAN_DOWNLOAD_REPORTS);
  const isStatementAllowed = getFromLocalStorage(LOCAL_STORAGE_KEYS.MERCHANT_STATEMENTS)?.enabled ?? false;
  const isDigestAllowed = getFromLocalStorage(LOCAL_STORAGE_KEYS.DIGEST)?.enabled ?? false;
  const canViewReportsTab = isReportsAllowed || isStatementAllowed || isDigestAllowed;
  const canViewReachTab = isAccessible(PERMISSIONS.CAN_VIEW_REACH) && isAllowedCountry(COUNTRIES.EGY);
  const isDevicesAllowed = isAccessible(PERMISSIONS.CAN_VIEW_TERMINAL);
  const isSummaryAllowed = isAccessible(PERMISSIONS.CAN_EXPORT_TERMINAL);
  const isDisputesAllowed = isAccessible(PERMISSIONS.CAN_MANAGE_DISPUTES);

  return [
    {
      ...getNavItemIdentifiers('innerLayout.navbar.home'),
      icon: House,
      route: HOME
    },
    ...(isTransfersAllowed ?
      [
        {
          ...getNavItemIdentifiers('innerLayout.navbar.balance'),
          icon: MoneyBill,
          tourElementId: getJoyRideElementId(5),
          subLinks: [
            {
              ...getNavItemIdentifiers('innerLayout.navbar.transfersOverview'),
              route: BALANCE,
            },
            {
              ...getNavItemIdentifiers('innerLayout.navbar.transfers'),
              route: TRANSFERS,
            }
          ]
        },
      ] : []
    ),
    ...(isBillsEnabled() ?
      [
        {
          label: t('innerLayout.navbar.bills'),
          icon: MoneySimpleFromBracket,
          route: INNER_ROUTES.BILLS
        }
      ]
      : []
    ),
    ...(canViewPaymentsTab ?
      [
        {
          ...getNavItemIdentifiers('innerLayout.navbar.payments'),
          icon: ArrowRightArrowLeft,
          subLinks: [
            ...(isTrnxAllowed ?
              [{
                ...getNavItemIdentifiers('innerLayout.navbar.transactions'),
                route: TRANSACTIONS,
                tourElementId: getJoyRideElementId(4)
              }] : []
            ),
            ...(isOrdersAllowed ?
              [{
                ...getNavItemIdentifiers('innerLayout.navbar.orders'),
                route: ORDERS
              }] : []
            ),
            ...(isChargebacksAllowed ?
              [{
                label: t('innerLayout.navbar.chargebacks'),
                route: CHARGE_BACKS
              }] : []
            ),
          ]
        },
      ] : []
    ),
    ...(canViewPaymentLinksTab ?
      [
        {
          ...getNavItemIdentifiers('innerLayout.navbar.paymentLinks'),
          icon: LinkSimple,
          subLinks: [
            ...(isQuickLinksAllowed ?
              [{
                ...getNavItemIdentifiers('innerLayout.navbar.quickLinks'),
                route: QUICK_LINKS,
              }] : []
            ),
            ...(isPaymeAllowed ?
              [{
                ...getNavItemIdentifiers('innerLayout.navbar.payme'),
                route: PAYME,
              }] : []
            ),
          ]
        },
      ] : []
    ),
    ...(canViewReachTab ?
      [{
        ...getNavItemIdentifiers('innerLayout.navbar.reach'),
        icon: Megaphone,
        subLinks: [
          {
            ...getNavItemIdentifiers('innerLayout.navbar.campaigns'),
            route: CAMPAIGNS,
          },
          {
            ...getNavItemIdentifiers('innerLayout.navbar.campaignsAnalysis'),
            route: CAMPAIGN_ANALYSIS,
          },
          {
            ...getNavItemIdentifiers('innerLayout.navbar.bundles'),
            route: BUNDLES,
          },
          {
            ...getNavItemIdentifiers('innerLayout.navbar.reachPortal'),
            route: "",
            selectable: false,
            specialAction: NAVBAR_SPECIAL_ACTIONS.REACH_PORTAL,
          }
        ]
      }] : []
    ),
    ...(isDisputesAllowed ?
      [{
        ...getNavItemIdentifiers('innerLayout.navbar.disputes'),
        icon: MoneyBillTransfer,
        route: DISPUTES
      }] : []
    ),
    ...(canViewReportsTab ?
      [{
        ...getNavItemIdentifiers('innerLayout.navbar.reports'),
        icon: ChartMixed,
        route: REPORTS
      }] : []
    ),
    ...(isMerchant() ?
      [{
        ...getNavItemIdentifiers('innerLayout.navbar.riskCenter'),
        icon: Shield,
        route: RISK_CENTER
      }] : []
    ),
    ...(isMerchant() ?
      [{
        ...getNavItemIdentifiers('innerLayout.navbar.terminals'),
        icon: MobileSignalOut,
        subLinks: [
          ...(isDevicesAllowed ?
            [{
              ...getNavItemIdentifiers('innerLayout.navbar.devicesPos'),
              route: DEVICES
            }] : []
          ),
          {
            ...getNavItemIdentifiers('innerLayout.navbar.branches'),
            route: BRANCHES
          },
          ...(isSummaryAllowed ?
            [{
              ...getNavItemIdentifiers('innerLayout.navbar.summary'),
              route: SUMMARY
            }] : []
          )
        ]
      }] : []
    ),
    {
      mobileOnlyLinks: true,
      ...getNavItemIdentifiers('innerLayout.navbar.settings'),
      icon: Gear,
      route: SETTINGS
    },
    {
      mobileOnlyLinks: true,
      hasSpecialAction: true,
      specialAction: NAVBAR_SPECIAL_ACTIONS.LANGUAGE,
      selectable: false,
      ...getNavItemIdentifiers('innerLayout.navbar.language'),
      icon: Globe,
      route: ""
    },
    ...(isAccessible(PERMISSIONS.CAN_VIEW_CHATBOT) ?
      [{
        hasSpecialAction: true,
        specialAction: NAVBAR_SPECIAL_ACTIONS.CHATBOT,
        selectable: false,
        ...getNavItemIdentifiers('innerLayout.navbar.chatbot'),
        icon: LifeRing,
        route: ""
      }] : []
    ),
    {
      mobileOnlyLinks: true,
      hasSpecialAction: true,
      ...getNavItemIdentifiers('innerLayout.navbar.support'),
      icon: Headset,
      selectable: false,
      // subLinks: [
      // {
      ...getNavItemIdentifiers('innerLayout.navbar.chatWithUs'),
      route: "",
      specialAction: NAVBAR_SPECIAL_ACTIONS.LIVE_CHAT
      // },
      // {
      //   ...getNavItemIdentifiers('innerLayout.navbar.support'),
      //   route: "",
      //   specialAction: NAVBAR_SPECIAL_ACTIONS.SUPPORT
      // }
      // ]
    },
    {
      hasSpecialAction: true,
      selectable: false,
      ...getNavItemIdentifiers('innerLayout.navbar.submitFeedback'),
      icon: CommentsQuestionCheck,
      specialAction: NAVBAR_SPECIAL_ACTIONS.FEEDBACK
    },
    {
      hasSpecialAction: true,
      selectable: false,
      ...getNavItemIdentifiers('innerLayout.navbar.switchToOldDashboard'),
      icon: ArrowsRepeat,
      specialAction: NAVBAR_SPECIAL_ACTIONS.SWITCH_DASHBOARD,
    }
  ]
};

export const pagesInternalRoutes = (t: TFunction) => ([
  {
    path: SINGLE_TRANSFER,
    targetLink: {
      nav: t('innerLayout.navbar.balance'),
      sub: t('innerLayout.navbar.transfers'),
    }
  },
  {
    path: SINGLE_TRANSACTION,
    targetLink: {
      nav: t('innerLayout.navbar.payments'),
      sub: t('innerLayout.navbar.transactions'),
    }
  },
  {
    path: SINGLE_ORDER,
    targetLink: {
      nav: t('innerLayout.navbar.payments'),
      sub: t('innerLayout.navbar.orders'),
    }
  },
  {
    path: SINGLE_CHARGE_BACK,
    targetLink: {
      nav: t('innerLayout.navbar.payments'),
      sub: t('innerLayout.navbar.chargebacks'),
    }
  },
  {
    path: SINGLE_QUICK_LINK,
    targetLink: {
      nav: t('innerLayout.navbar.paymentLinks'),
      sub: t('innerLayout.navbar.quickLinks'),
    }
  },
  {
    path: PAYME_SINGLE_TRANSACTION,
    targetLink: {
      nav: t('innerLayout.navbar.paymentLinks'),
      sub: t('innerLayout.navbar.payme'),
    }
  },
  {
    path: PAYME_SETTINGS,
    targetLink: {
      nav: t('innerLayout.navbar.paymentLinks'),
      sub: t('innerLayout.navbar.payme'),
    }
  },
  {
    path: SINGLE_CAMPAIGN,
    targetLink: {
      nav: t('innerLayout.navbar.reach'),
      sub: t('innerLayout.navbar.campaigns'),
    }
  },
  {
    path: SINGLE_RISK_ITEM,
    targetLink: {
      nav: t('innerLayout.navbar.riskCenter'),
    }
  },
  {
    path: SINGLE_DISPUTE,
    targetLink: {
      nav: t('innerLayout.navbar.disputes'),
    }
  },
]);

export const createBtnLinks: (t: TFunction) => ICreateBtnLinks[] = (t) => {
  const canCreateQuickLink = isStaff() ? isAccessible(PERMISSIONS.CAN_CREATE_QUICK_LINKS) : true;

  return [
    // {
    //   icon: Qrcode,
    //   text: t('innerLayout.navbar.create.qrCode'),
    //   route: CREATE_QRCODE_PAYMENT,
    //   trackingLabel: SHARED_EVENTS_VALUES.QR_CODE_PAYMENT
    // },
    ...(
      canCreateQuickLink ? [
        {
          icon: Users,
          text: t('innerLayout.navbar.create.bulkPaymentLinks'),
          route: BULK_PAYMENT_LINKS,
          label: t('innerLayout.paymentLinksCreationPages.bulkPaymentLinks.title'),
          trackingLabel: SHARED_EVENTS_VALUES.BULK_PAYMENT_LINK
        },
        {
          icon: LinkSimple,
          text: t('innerLayout.navbar.create.singlePaymentLink'),
          route: SINGLE_PAYMENT_LINK,
          label: t('innerLayout.paymentLinksCreationPages.singlePaymentLink.title'),
          trackingLabel: SHARED_EVENTS_VALUES.PAYMENT_LINK
        }
      ] : []
    ),
  ]
};

export const accountBarLinks: (t: TFunction) => IAccountBarLinks[] = (t) => ([
  // {
  //   label: t('innerLayout.navbar.editProfile'),
  //   icon: PenToSquare,
  //   action: "edit-profile"
  // },
  {
    label: t('innerLayout.navbar.logout'),
    icon: ArrowRightFromBracket,
    action: "logout"
  }
]);

export const supportLinks: (t: TFunction) => ISupportLinks[] = (t) => ([
  {
    label: t('innerLayout.navbar.chatWithUs'),
    icon: CommentsQuestionCheck,
    action: "live-chat"
  },
  // {
  //   label: t('innerLayout.navbar.support'),
  //   icon: LifeRing,
  //   action: "support"
  // }
]);